/**
 * Screen-scoped design tokens for Settings → My Plan ("Plan & Billing").
 *
 * Every colour, size, radius, shadow and type step used by the Plan & Billing
 * screen is captured here exactly as specified by the design so the components
 * never carry a raw literal. These stay screen-local on purpose — once every
 * screen is re-skinned the common values get promoted into the MUI theme.
 */

import { contentCard, formField, inkfree, notification, signer, switchControl, white } from "theme/colors";

export const myPlanTokens = {
  color: {
    /** Deep brand ink — headings, plan name, table body text. */
    ink: signer.ink,
    /** Secondary copy — card descriptions, invoice cells. */
    body: signer.text.meta,
    /** Muted copy — stat labels, stat captions, column headers, icons. */
    muted: signer.text.faint,
    surface: white,
    /** Card chrome. */
    cardBorder: contentCard.borderMuted,
    /** Divider between list rows / card sections. */
    rowDivider: signer.border.divider,
    /** Column-header strip. */
    headerBg: signer.surface.subtle,
    headerBorder: signer.border.light,
    /** Outlined control border (secondary button). */
    controlBorder: formField.border,
    /** Unselected radio ring. */
    radioBorder: switchControl.trackOff,
    /** Link text/hover — a thin accent, stays orange. */
    accent: signer.orange.main,
    accentHover: signer.orange.dark,
    /**
     * Primary action button + selected payment radio — solid fills, so
     * per the standing brand-colour rule these are the app brand red, not
     * `accent` above.
     */
    ctaBg: inkfree.main,
    ctaBgHover: inkfree.dark,
    onAccent: white,
    /** Subtle hover fill (pagination items, ghost controls). */
    hoverFill: signer.surface.muted,
    /** Unlimited / paid / positive. */
    successText: notification.success.icon,
    successBg: notification.success.bg,
    /** Expired / failed / negative. */
    dangerText: notification.error.icon,
    dangerBg: notification.error.bg,
    /** Billing-cycle badge next to the plan name. */
    cycleBadgeText: signer.purple.main,
    cycleBadgeBg: signer.surface.muted,
    /** Usage meter. */
    meterTrack: signer.border.light,
    meterFill: signer.orange.main,
    meterFillHigh: notification.error.icon,
    /** Card-brand plate. */
    brandPlateBg: signer.surface.muted,
    brandPlateBorder: contentCard.borderMuted,
    /** Disabled pagination arrow. */
    disabledControl: switchControl.trackOff,
  },

  /** Card-brand plate label colours, keyed by the Stripe brand id. */
  cardBrand: {
    visa: "#1A1F71",
    mastercard: signer.gradient.amber,
    amex: "#2E77BC",
    discover: signer.purple.main,
    unionpay: signer.gradient.coral,
    diners: "#0079BE",
    jcb: "#0E4C96",
    cartes_bancaires: "#227D77",
    fallback: signer.text.meta,
  } as Record<string, string>,

  /** Short plate labels shown on the brand chip (design uses text, not logos). */
  cardBrandLabel: {
    visa: "VISA",
    mastercard: "MC",
    amex: "AMEX",
    discover: "DISC",
    unionpay: "UNION",
    diners: "DINERS",
    jcb: "JCB",
    cartes_bancaires: "CB",
    fallback: "CARD",
  } as Record<string, string>,

  font: {
    planName: "21px",
    statValue: "22px",
    sectionTitle: "15px",
    sectionDesc: "12.5px",
    rowText: "13px",
    button: "12.5px",
    statUnit: "12px",
    statCaption: "11px",
    columnHeader: "10.5px",
    chip: "10.5px",
    cycleBadge: "10px",
    brandPlate: "8.5px",
    pageItem: "12px",
  },

  weight: {
    heading: 800,
    strong: 700,
    medium: 600,
    regular: 400,
  },

  letterSpacing: {
    heading: "-0.01em",
    columnHeader: "0.09em",
    statLabel: "0.1em",
    cycleBadge: "0.08em",
  },

  radius: {
    card: "14px",
    control: "9px",
    plate: "5px",
    pageItem: "7px",
    pill: "999px",
  },

  border: {
    hairline: "1px",
    control: "1.5px",
  },

  space: {
    /** Gap between the stacked Plan & Billing cards. */
    section: "20px",
    /** Horizontal padding shared by every card row. */
    gutter: "28px",
    planHeaderY: "24px",
    statCellY: "22px",
    sectionHeaderTop: "20px",
    sectionHeaderBottom: "16px",
    columnHeaderY: "10px",
    rowY: "13px",
    footerY: "14px",
  },

  size: {
    buttonHeight: "40px",
    smallButtonHeight: "38px",
    radio: "16px",
    radioDot: "8px",
    plateWidth: "38px",
    plateHeight: "24px",
    /** Card-network logo image in the payment-methods list. */
    cardLogoWidth: "40px",
    rowIcon: "15px",
    pageItem: "28px",
    pageArrow: "11px",
    meterHeight: "5px",
  },

  shadow: {
    card: "0 1px 3px rgba(33, 21, 71, 0.04)",
  },

  /** Grid templates copied verbatim from the design. */
  grid: {
    paymentRow: "110px 1fr 60px",
    invoiceRow: "1.4fr 1fr 1fr 1fr 110px 90px 70px",
    statStrip: "repeat(4, 1fr)",
  },
} as const;

/**
 * Phone and tablet values for **Settings → My Plan**, traced from
 * `Settings - Mobile & Tablet.dc.html`.
 *
 * The reference draws 375, 390 and 834 identically here — only the gutters
 * around the cards change, and `SettingsLayout` already owns those — so this is
 * one set of values rather than a phone/tablet split. Applied below `md`; the
 * desktop screen is untouched.
 */
export const myPlanResponsiveTokens = {
  /** Card padding shared by every header, stat cell and list row. */
  gutter: "16px",
  headerY: "16px",
  headerGap: "11px",
  /** Payment/Invoices header: title, sentence, then the action. */
  sectionHeaderGap: "10px",
  sectionHeaderStackedGap: "3px",
  planNameFontSize: "20px",
  sectionTitleFontSize: "14.5px",
  descFontSize: "12px",
  cyclePillFontSize: "9.5px",
  cyclePillPaddingY: "3px",

  /** "Change plan" + "Cancel" run as one full-width row. */
  actionGap: "8px",
  buttonHeight: "44px",
  buttonPaddingX: "20px",
  buttonFontSize: "13px",
  ghostPaddingX: "16px",
  ghostFontSize: "12.5px",

  stat: {
    padding: "14px 16px",
    gap: "4px",
    labelFontSize: "10px",
    valueFontSize: "20px",
    unitFontSize: "11px",
    captionFontSize: "11px",
  },

  row: {
    padding: "13px 16px",
    gap: "11px",
    textGap: "3px",
    chipFontSize: "10px",
    chipPaddingX: "8px",
  },

  invoice: {
    padding: "14px 16px",
    gap: "12px",
    textGap: "4px",
    idFontSize: "13px",
    subFontSize: "11.5px",
    chipFontSize: "10px",
    /** Bordered square holding the download glyph. */
    buttonSize: "38px",
    buttonRadius: "9px",
    buttonIcon: "15px",
  },
} as const;

export default myPlanTokens;
